import { providers } from '../../providers';
import { baidu } from '../baidu';
import type { VideoModelEntry } from '../types';

// 价格表: 人民币/秒
// C1 / P6
const PIXVERSE_C1 = { '360p': 0.1667, '540p': 0.2222, '720p': 0.2778, '1080p': 0.5278 };
const PIXVERSE_C1_AUDIO = { '360p': 0.2222, '540p': 0.2778, '720p': 0.3611, '1080p': 0.6667 };
const PIXVERSE_V6 = { '360p': 0.1389, '540p': 0.1944, '720p': 0.25, '1080p': 0.5 };
const PIXVERSE_V6_AUDIO = { '360p': 0.1944, '540p': 0.25, '720p': 0.3333, '1080p': 0.6389 };
// V5.6
const PIXVERSE_V56: Record<string, Record<number, number>> = {
    '360p':  { 1: 0.5,    2: 0.5833, 3: 0.6944, 4: 0.7778, 5: 0.9722, 6: 1.2778, 7: 1.5556, 8: 1.9444, 9: 2.0556, 10: 2.1389 },
    '540p':  { 1: 0.5,    2: 0.5833, 3: 0.6944, 4: 0.7778, 5: 0.9722, 6: 1.2778, 7: 1.5556, 8: 1.9444, 9: 2.0556, 10: 2.1389 },
    '720p':  { 1: 0.6389, 2: 0.75,   3: 0.8889, 4: 1,      5: 1.25,   6: 1.6389, 7: 2,      8: 2.5,    9: 2.6389, 10: 2.75 },
    '1080p': { 1: 1.0556, 2: 1.25,   3: 1.4722, 4: 1.6667, 5: 2.0833, 6: 2.7222, 7: 3.3333, 8: 4.1667, 9: 4.3889, 10: 4.5833 },
};
// V5.6 声音开关
const PIXVERSE_V56_SOUND = 1.25;
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality ?? ''] ?? Math.max(...Object.values(table));
const lookupV56 = (quality: string | undefined, duration: number, withAudio: boolean) => {
    const q = quality ?? '720p';
    const base = PIXVERSE_V56[q]?.[duration] ?? PIXVERSE_V56['720p'][5];
    return base + (withAudio ? PIXVERSE_V56_SOUND : 0);
};
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;

const COST_FACTOR = 1.5;

export const pixverseModels: VideoModelEntry[] = [
    {
        id: 'pixverse-c1',
        chef: providers.pixverse,
        providers: [
            {
                ...providers.pixverse,
                model: baidu('pixverse-c1'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(duration * lookup(withAudio ? PIXVERSE_C1_AUDIO : PIXVERSE_C1, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(duration * lookup(withAudio ? PIXVERSE_C1_AUDIO : PIXVERSE_C1, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'pixverse-v6',
        chef: providers.pixverse,
        providers: [
            {
                ...providers.pixverse,
                model: baidu('pixverse-v6'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(duration * lookup(withAudio ? PIXVERSE_V6_AUDIO : PIXVERSE_V6, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(duration * lookup(withAudio ? PIXVERSE_V6_AUDIO : PIXVERSE_V6, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['21:9', '16:9', '3:2', '4:3', '1:1', '3:4', '2:3', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['360p', '540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'pixverse-v5.6',
        chef: providers.pixverse,
        providers: [
            {
                ...providers.pixverse,
                model: baidu('pixverse-v5.6'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(lookupV56(quality, duration, withAudio)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withAudio = providerOptions?.generate_audio_switch !== false;
                    return usd(lookupV56(quality, duration, withAudio)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '540p', '720p', '1080p'],
                qualityByDuration: {
                    10: ['360p', '540p', '720p'],
                },
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '540p', '720p', '1080p'],
                qualityByDuration: {
                    10: ['360p', '540p', '720p'],
                },
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '540p', '720p', '1080p'],
                qualityByDuration: {
                    10: ['360p', '540p', '720p'],
                },
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                qualities: ['360p', '540p', '720p', '1080p'],
                qualityByDuration: {
                    10: ['360p', '540p', '720p'],
                },
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];